/**
 * ChatDrawer — Right-side slide drawer for chatting about a candidate
 *
 * Features:
 * - Message history with user/assistant bubbles
 * - Real-time streaming with typing indicator
 * - Stale context banner
 * - Conversation selector
 * - Safe markdown rendering via DOMPurify + react-markdown
 */

import { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import DOMPurify from 'dompurify';
import { useChat } from '../hooks/useChat';
import { Button } from './ui';
import {
  X,
  Send,
  Plus,
  Trash2,
  AlertTriangle,
  MessageCircle,
  Loader2,
  ChevronDown,
  Bookmark,
  Check,
} from 'lucide-react';

interface ChatDrawerProps {
  applicationId: number;
  candidateName: string;
  isOpen: boolean;
  onClose: () => void;
}

function SafeMarkdown({ content }: { content: string }) {
  const sanitized = DOMPurify.sanitize(content, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'ul', 'ol', 'li', 'code', 'pre', 'h1', 'h2', 'h3', 'h4', 'blockquote'],
    ALLOWED_ATTR: [],
  });
  return (
    <div className="prose prose-sm dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
      <ReactMarkdown>{sanitized}</ReactMarkdown>
    </div>
  );
}

export function ChatDrawer({ applicationId, candidateName, isOpen, onClose }: ChatDrawerProps) {
  const {
    conversations,
    activeConversation,
    messages,
    isStreaming,
    streamingContent,
    error,
    isLoading,
    suggestions,
    loadConversations,
    createConversation,
    selectConversation,
    sendMessage,
    saveInsight,
    deleteConversation,
    stopStreaming,
  } = useChat(applicationId);

  const [input, setInput] = useState('');
  const [showConvSelector, setShowConvSelector] = useState(false);
  const [savedMsgIds, setSavedMsgIds] = useState<Set<number>>(new Set());
  const [savingMsgId, setSavingMsgId] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Load conversations on open
  useEffect(() => {
    if (isOpen) {
      loadConversations();
    }
  }, [isOpen, loadConversations]);

  // Auto-create conversation if none exist
  useEffect(() => {
    if (isOpen && conversations.length === 0 && !activeConversation && !isLoading) {
      createConversation();
    }
  }, [isOpen, conversations.length, activeConversation, isLoading, createConversation]);

  // Select first conversation if none active
  useEffect(() => {
    if (isOpen && conversations.length > 0 && !activeConversation) {
      selectConversation(conversations[0].id);
    }
  }, [isOpen, conversations, activeConversation, selectConversation]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  // Focus input when drawer opens
  useEffect(() => {
    if (isOpen && !isStreaming) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen, isStreaming]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || isStreaming) return;
    setInput('');
    sendMessage(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSaveInsight = async (msgId: number) => {
    setSavingMsgId(msgId);
    const result = await saveInsight([msgId]);
    if (result) {
      setSavedMsgIds(prev => new Set(prev).add(msgId));
    }
    setSavingMsgId(null);
  };

  const handleNewConversation = () => {
    createConversation();
    setShowConvSelector(false);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 dark:bg-black/40 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white dark:bg-gray-800 shadow-xl z-50 flex flex-col transition-transform">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="flex items-center gap-2 min-w-0">
            <MessageCircle className="h-5 w-5 text-blue-500 flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                Chat about {candidateName}
              </h2>
              {activeConversation && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {activeConversation.modelTier === 'premium' ? 'Premium' : 'Standard'} model
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {/* Conversation selector */}
            {conversations.length > 1 && (
              <div className="relative">
                <button
                  onClick={() => setShowConvSelector(!showConvSelector)}
                  className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                  title="Switch conversation"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
                {showConvSelector && (
                  <div className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-gray-700 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 py-1 z-50">
                    {conversations.map(conv => (
                      <button
                        key={conv.id}
                        onClick={() => { selectConversation(conv.id); setShowConvSelector(false); }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-600 flex items-center justify-between ${
                          conv.id === activeConversation?.id ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <span className="truncate">
                          {conv.title || `Chat #${conv.id}`}
                        </span>
                        <span className="text-xs text-gray-400 ml-2">{conv.messageCount} msgs</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <button
              onClick={handleNewConversation}
              className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              title="New conversation"
            >
              <Plus className="h-4 w-4" />
            </button>
            {activeConversation && (
              <button
                onClick={() => deleteConversation(activeConversation.id)}
                className="p-1.5 rounded-md text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                title="Delete conversation"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Stale context banner */}
        {activeConversation?.hasStaleContext && (
          <div className="px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
            <span className="text-xs text-amber-700 dark:text-amber-300">
              New info available — will be included in your next message
            </span>
          </div>
        )}

        {/* Messages area */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {messages.length === 0 && !isStreaming && !isLoading && (
            <div className="text-center py-8">
              <MessageCircle className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Ask anything about this candidate
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 mb-4">
                Resume, scores, criteria, feedback — it's all in context
              </p>
              {suggestions && suggestions.questions.length > 0 && (
                <div className="space-y-2 px-2">
                  {suggestions.questions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(q)}
                      className="w-full text-left text-sm px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-700 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                  {suggestions.commands.length > 0 && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
                      Deep-dive: {suggestions.commands.map(c => (
                        <code key={c.command} className="mx-0.5 px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">{c.command}</code>
                      ))}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {messages.map(msg => (
            <div
              key={msg.id}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} group`}
            >
              <div className="flex flex-col max-w-[85%]">
                <div
                  className={`rounded-lg px-3 py-2 ${
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                  }`}
                >
                  {msg.role === 'user' ? (
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  ) : (
                    <SafeMarkdown content={msg.content} />
                  )}
                </div>
                {msg.role === 'assistant' && (
                  <div className="flex justify-end mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    {savedMsgIds.has(msg.id) ? (
                      <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 px-1">
                        <Check className="h-3 w-3" /> Saved
                      </span>
                    ) : (
                      <button
                        onClick={() => handleSaveInsight(msg.id)}
                        disabled={savingMsgId === msg.id}
                        className="flex items-center gap-1 text-xs text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 px-1 transition-colors"
                        title="Save as feedback note"
                      >
                        {savingMsgId === msg.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Bookmark className="h-3 w-3" />
                        )}
                        Save as note
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Streaming message */}
          {isStreaming && streamingContent && (
            <div className="flex justify-start">
              <div className="max-w-[85%] rounded-lg px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <SafeMarkdown content={streamingContent} />
              </div>
            </div>
          )}

          {/* Typing indicator */}
          {isStreaming && !streamingContent && (
            <div className="flex justify-start">
              <div className="rounded-lg px-3 py-2 bg-gray-100 dark:bg-gray-700">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}

          {/* Error display */}
          {error && (
            <div className="px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3">
          <div className="flex gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about this candidate..."
              disabled={isStreaming || !activeConversation}
              rows={1}
              className="flex-1 resize-none rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              style={{ maxHeight: '120px' }}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = Math.min(target.scrollHeight, 120) + 'px';
              }}
            />
            {isStreaming ? (
              <Button
                variant="secondary"
                onClick={stopStreaming}
                className="flex-shrink-0"
              >
                <Loader2 className="h-4 w-4 animate-spin" />
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleSend}
                disabled={!input.trim() || !activeConversation}
                className="flex-shrink-0"
              >
                <Send className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Footer: cost + message count */}
          {activeConversation && activeConversation.messageCount > 0 && (
            <div className="flex justify-between mt-2 text-xs text-gray-400 dark:text-gray-500">
              <span>{activeConversation.messageCount} messages</span>
              <span>${activeConversation.totalCostUsd.toFixed(4)} cost</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
